import { PageContainer, PageHeader } from '@/components/Atoms'
import { useFederalExciseLastUpdated } from '@/hooks/queries/crt/useFederalExcise'
import { useProductTypeMappingsLastUpdated } from '@/hooks/queries/crt/useProductTypes'
import { useProductTypeVariantSummary } from '@/hooks/queries/crt/useProductTypeVariant'
import { toUpdatedAt } from '@/lib/utils/getLastUpdatedState'
import { Insights, type InsightItem } from '@/routes/_auth/_modules/_shared/-components/Insights'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_auth/_modules/crt/materials/insights/')({
  component: MaterialsInsight,
})

function MaterialsInsight() {
  const { t } = useTranslation(['crt', 'common'])

  const { data: productTypeVariantSummary } = useProductTypeVariantSummary()
  const { data: federalExciseLastUpdated } = useFederalExciseLastUpdated()
  const { data: productTypesLastUpdated } = useProductTypeMappingsLastUpdated()

  const automatedInsights: InsightItem[] = [
    {
      name: t('route.crt.productTypeVariants', { ns: 'common' }),
      frequency: t('insights.frequency.daily'),
      frequencySchedule: { type: 'daily' },
      updatedAt: toUpdatedAt(productTypeVariantSummary),
      description: t('materialsInsight.productTypeVariants.linkText'),
      link: { to: '/crt/materials/insights/product-type-variants' },
      permissions: ['crt_product_type_variant_view'],
    },
  ]

  const assumptions: InsightItem[] = [
    {
      name: t('route.crt.unreleasedMaterials', { ns: 'common' }),
      description: t('materialsInsight.unreleasedMaterials.linkText'),
      link: { to: '/crt/materials/insights/unreleased-materials' },
      permissions: ['crt_unreleased_materials_view'],
    },
    {
      name: t('route.crt.federalExcise', { ns: 'common' }),
      description: t('materialsInsight.federalExcise.linkText'),
      updatedAt: toUpdatedAt(federalExciseLastUpdated),
      link: { to: '/crt/materials/insights/federal-excise' },
      permissions: ['crt_federal_excise_view'],
    },
    {
      name: t('route.crt.productTypes', { ns: 'common' }),
      description: t('materialsInsight.productTypes.linkText'),
      updatedAt: toUpdatedAt(productTypesLastUpdated),
      link: { to: '/crt/materials/insights/product-types' },
      permissions: ['crt_product_types_view'],
    },
    {
      name: t('route.crt.materialGroupOverrides', { ns: 'common' }),
      description: t('materialsInsight.materialGroupOverrides.linkText'),
      link: { to: '/crt/materials/insights/material-groups-overrides' },
      permissions: ['crt_material_group_overrides_view'],
    },
  ]

  return (
    <PageContainer>
      <PageHeader>{t('materialsInsight.pageHeader')}</PageHeader>
      <Insights automatedImports={automatedInsights} assumptions={assumptions} />
    </PageContainer>
  )
}
